import { assertStaffCapability } from "./authorization.js";
import { isFulfilmentModeEnabled, } from "./catalog.js";
import { validateCatalogPricing } from "./catalog-pricing.js";
import { DomainValidationError } from "./identifiers.js";
import { checkExpectedVersion, requireReason, violation } from "./transition-support.js";
export const catalogCommands = Object.freeze([
    "submit_for_review",
    "return_to_draft",
    "publish",
    "unpublish",
    "archive",
]);
const rule = (id, from, to, command, capability, reasonRequired = false) => Object.freeze({ id, from, to, command, capability, reasonRequired });
/**
 * The nine allowed transitions. Publishing goes through REVIEW (no two-person rule: the same editor
 * may submit and publish), and a published item is unpublished before it is archived. Every rule is
 * a catalog editor's work: no other role, and no customer or system actor, holds these capabilities.
 * The database guard (LF016) allows exactly these (from, to) pairs.
 */
export const catalogTransitions = Object.freeze([
    rule("C1", "DRAFT", "REVIEW", "submit_for_review", "catalog.edit"),
    rule("C2", "REVIEW", "DRAFT", "return_to_draft", "catalog.edit"),
    rule("C3", "REVIEW", "PUBLISHED", "publish", "catalog.publish"),
    rule("C4", "PUBLISHED", "UNPUBLISHED", "unpublish", "catalog.publish", true),
    rule("C5", "UNPUBLISHED", "PUBLISHED", "publish", "catalog.publish"),
    rule("C6", "UNPUBLISHED", "DRAFT", "return_to_draft", "catalog.edit"),
    rule("C7", "DRAFT", "ARCHIVED", "archive", "catalog.publish", true),
    rule("C8", "REVIEW", "ARCHIVED", "archive", "catalog.publish", true),
    rule("C9", "UNPUBLISHED", "ARCHIVED", "archive", "catalog.publish", true),
]);
export function isAllowedCatalogTransition(from, to) {
    return catalogTransitions.some((row) => row.from === from && row.to === to);
}
// --- Publishability ----------------------------------------------------------------------------------
export const publishabilityIssueCodes = Object.freeze([
    "MISSING_SUBTYPE",
    "MISSING_SHORT_DESCRIPTION",
    "MISSING_DESCRIPTION",
    "MISSING_LOCATION",
    "MISSING_COVER_IMAGE",
    "MEDIA_ALT_TEXT_MISSING",
    "MISSING_PRIMARY_CATEGORY",
    "DINING_MISSING_CUISINE",
    "VILLA_MISSING_CAPACITY",
    "VILLA_TIMES_WITHOUT_ZONE",
    "EVENT_MISSING_VENUE",
    "EVENT_ENDED",
    "EXPERIENCE_MISSING_DURATION",
    "SERVICE_MISSING_AREA",
    "FULFILMENT_MODE_NOT_ENABLED",
    "EXTERNAL_LINK_MISSING",
    "PRICING_INCOMPLETE",
    "CUSTOMER_TEXT_CONTAINS_INTERNAL_NOTE",
    "EXTERNAL_URL_NOT_ALLOWED",
    "PRICE_AUTHORITY_EXPIRED",
]);
const unset = (value) => value === null || value === undefined;
const blank = (value) => unset(value) || value.trim() === "";
/**
 * Whether an item may be PUBLISHED, pure and ordered. A BLOCKING issue refuses publication; a
 * WARNING is reported and allowed. The database re-enforces the subset it can see (the subtype row,
 * the core text and location, the cover image with alt text, the type-required columns, the villa
 * times with a zone, and a fulfilment mode that is enabled at all); taxonomy (categories, cuisine
 * tags), an ended event, external links, an expired payment-grade mark and a link on a concierge
 * item are judged here. No internal note is ever read: the caller passes only whether any note text
 * was found in a customer field.
 */
export function assessPublishability(aggregate) {
    const issues = [];
    const add = (code, severity = "BLOCKING") => issues.push({ code, severity });
    const subtype = aggregate.subtype;
    if (subtype === null)
        add("MISSING_SUBTYPE");
    if (blank(aggregate.shortDescription))
        add("MISSING_SHORT_DESCRIPTION");
    if (blank(aggregate.description))
        add("MISSING_DESCRIPTION");
    if (blank(aggregate.countryCode) || blank(aggregate.locality))
        add("MISSING_LOCATION");
    if (!aggregate.media.some((media) => media.kind === "IMAGE" && media.isCover)) {
        add("MISSING_COVER_IMAGE");
    }
    if (aggregate.media.some((media) => media.kind === "IMAGE" && blank(media.altText))) {
        add("MEDIA_ALT_TEXT_MISSING");
    }
    if (!aggregate.categories.some((category) => category.isPrimary))
        add("MISSING_PRIMARY_CATEGORY");
    switch (aggregate.itemType) {
        case "DINING":
            if (!aggregate.tags.some((tag) => tag.namespace === "CUISINE"))
                add("DINING_MISSING_CUISINE");
            break;
        case "VILLA":
            if (subtype !== null && (unset(subtype.bedrooms) || unset(subtype.maxGuests))) {
                add("VILLA_MISSING_CAPACITY");
            }
            if (subtype !== null &&
                (!blank(subtype.checkInTime) || !blank(subtype.checkOutTime)) &&
                blank(aggregate.timeZone)) {
                add("VILLA_TIMES_WITHOUT_ZONE");
            }
            break;
        case "EVENT":
            if (subtype !== null && blank(subtype.venueName))
                add("EVENT_MISSING_VENUE");
            if (subtype !== null) {
                const end = subtype.endsAt ?? subtype.startsAt ?? null;
                if (end !== null && end.getTime() < aggregate.now.getTime())
                    add("EVENT_ENDED", "WARNING");
            }
            break;
        case "EXPERIENCE":
            if (subtype !== null && unset(subtype.durationMinutes))
                add("EXPERIENCE_MISSING_DURATION");
            break;
        case "SERVICE":
            if (subtype !== null && blank(subtype.serviceAreaText))
                add("SERVICE_MISSING_AREA");
            break;
    }
    if (!isFulfilmentModeEnabled(aggregate.fulfilmentMode))
        add("FULFILMENT_MODE_NOT_ENABLED");
    if ((aggregate.fulfilmentMode === "EXTERNAL_LINK" ||
        aggregate.fulfilmentMode === "CUSTOMER_SELF_SERVICE") &&
        blank(aggregate.externalUrl)) {
        add("EXTERNAL_LINK_MISSING");
    }
    if (aggregate.fulfilmentMode === "HUMAN_CONCIERGE" && !blank(aggregate.externalUrl)) {
        add("EXTERNAL_URL_NOT_ALLOWED");
    }
    try {
        validateCatalogPricing(aggregate.pricing);
    }
    catch (error) {
        if (!(error instanceof DomainValidationError))
            throw error;
        add("PRICING_INCOMPLETE");
    }
    if (aggregate.customerTextCopiesInternalNote === true) {
        add("CUSTOMER_TEXT_CONTAINS_INTERNAL_NOTE");
    }
    // A payment-grade mark whose validity already ended is not payable (resolution refuses it), so
    // publishing is allowed; the editor should know the price needs a new approval.
    const { validUntil } = aggregate.pricing;
    if (aggregate.priceAuthoritative === true &&
        validUntil !== null &&
        validUntil.getTime() <= aggregate.now.getTime()) {
        add("PRICE_AUTHORITY_EXPIRED", "WARNING");
    }
    return Object.freeze(issues);
}
export const hasBlockingPublishabilityIssue = (issues) => issues.some((issue) => issue.severity === "BLOCKING");
// --- Internal note text in customer fields -----------------------------------------------------------
/** A note, or a line of one, shorter than this is too common a phrase to count as a copy. */
const MIN_NOTE_FRAGMENT = 24;
const normalizeText = (value) => value.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
/**
 * Whether any internal note text was copied into a customer-visible field. Text is compared after
 * NFKC, lower case and whitespace collapse. A note body of at least 24 characters, or any line of a
 * note of at least 24 characters, that appears inside a customer text counts. A heuristic that
 * catches the common copy-paste, not a proof: the guarantee that notes stay internal is structural
 * (their own table, no customer type or query reaches it). Returns a flag, never text.
 */
export function findInternalNoteTextInCustomerFields(customerTexts, noteBodies) {
    const haystacks = customerTexts.map(normalizeText).filter((text) => text !== "");
    if (haystacks.length === 0)
        return false;
    for (const body of noteBodies) {
        const fragments = [body, ...body.split(/\r?\n/)]
            .map(normalizeText)
            .filter((fragment) => [...fragment].length >= MIN_NOTE_FRAGMENT);
        if (fragments.some((fragment) => haystacks.some((text) => text.includes(fragment))))
            return true;
    }
    return false;
}
const AUDIT = {
    submit_for_review: "catalog_item.submitted_for_review",
    return_to_draft: "catalog_item.returned_to_draft",
    publish: "catalog_item.published",
    unpublish: "catalog_item.unpublished",
    archive: "catalog_item.archived",
};
/**
 * Plans one of the nine allowed transitions, or refuses it. Checks run in this order: (1) the actor
 * (only a catalog editor; a customer, a system actor or any other role is `role_missing`), (2) the
 * pair (`catalog.archived` from ARCHIVED, else `catalog.transition_invalid`), (3) `expectedVersion`,
 * (4) the staff reason (unpublish and archive), then (5) for a publish, `assessPublishability` of
 * the aggregate (`catalog.not_publishable` on a blocking issue). Nothing is written here.
 */
export function planCatalogTransition(input) {
    const { item, to, command, actor } = input;
    assertStaffCapability(actor, "catalog.edit", input.environment);
    if (item.status === "ARCHIVED")
        throw violation("catalog.archived");
    const matched = catalogTransitions.find((row) => row.from === item.status && row.to === to);
    if (matched === undefined || matched.command !== command) {
        throw violation("catalog.transition_invalid");
    }
    assertStaffCapability(actor, matched.capability, input.environment);
    checkExpectedVersion(actor, input.expectedVersion, item.version, "catalog.version_conflict");
    if (matched.reasonRequired)
        requireReason(input.reason);
    let warningCount = 0;
    if (command === "publish") {
        if (input.aggregate === null)
            throw new DomainValidationError("aggregate", "is required to publish");
        const issues = assessPublishability(input.aggregate);
        if (hasBlockingPublishabilityIssue(issues))
            throw violation("catalog.not_publishable");
        warningCount = issues.length;
    }
    const patch = command === "publish"
        ? item.firstPublishedAt === null
            ? { kind: "set_status", status: to, publishedAt: "now", firstPublishedAt: "now" }
            : { kind: "set_status", status: to, publishedAt: "now" }
        : { kind: "set_status", status: to };
    return Object.freeze({
        ruleId: matched.id,
        command,
        from: item.status,
        to,
        patch,
        auditAction: AUDIT[command],
        warningCount,
    });
}
